/**
 * Shared catalog search: strict matching + popularity-aware ranking for navbar
 * search, mega-menu game lists, and any other game-picker UIs.
 */

/** Default ordering for search / quick links (more popular first). */
export const NAVBAR_SEARCH_POPULAR_ORDER = [
  'Steal a Brainrot',
  'Grand Theft Auto 5',
  'Old School RuneScape',
  'Fortnite',
  'Roblox',
  'Valorant',
  'EA Sports FC',
  'Rainbow Six Siege X',
  'DonutSMP',
  'Grow a Garden',
  'Counter-Strike 2',
  'League of Legends',
  'Apex Legends',
  'Escape from Tarkov',
];

export function popularRank(game, popularOrder = NAVBAR_SEARCH_POPULAR_ORDER) {
  const i = popularOrder.indexOf(game);
  return i === -1 ? 500 : i;
}

/**
 * @param {string} game - Canonical game display name
 * @param {string} query - Lowercased trimmed query
 * @param {Record<string, string>} gameAliases - alias -> canonical name
 */
/** Strip common category/service words so queries like "mobile legends boost" still match the game name. */
function stripSearchCategoryNoise(q) {
  return q
    .replace(/\b(boost|boosting|rank|ranked|wins?|net\s*wins?|achievements?|coaching|cheap|buy|sell|service)\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function matchesGame(game, query, gameAliases) {
  const gameLower = game.toLowerCase();
  const queryLower = query.toLowerCase().trim();

  if (gameAliases[queryLower] === game) {
    return true;
  }

  if (queryLower.length >= 3 && gameLower.includes(queryLower)) {
    return true;
  }

  const queryStripped = stripSearchCategoryNoise(queryLower);
  if (
    queryStripped &&
    queryStripped !== queryLower &&
    queryStripped.length >= 2 &&
    gameLower.includes(queryStripped)
  ) {
    return true;
  }

  for (const [alias, mappedGame] of Object.entries(gameAliases)) {
    if (mappedGame !== game) continue;
    if (alias === queryLower || alias.startsWith(queryLower)) return true;
    if (queryLower.length >= 3 && alias.includes(queryLower)) return true;
  }

  const gameWords = gameLower.split(/[\s-]+/).filter(Boolean);
  const queryWords = queryLower.split(/[\s-]+/).filter(Boolean);

  const allQueryWordsMatch = queryWords.every((qWord) =>
    gameWords.some(
      (gWord) =>
        gWord.startsWith(qWord) || (qWord.length >= 3 && gWord.includes(qWord))
    )
  );

  if (allQueryWordsMatch && queryWords.length > 0) {
    return true;
  }

  const gameAcronym = gameWords.map((w) => w[0]).join('');
  if (
    queryLower.length >= 2 &&
    (gameAcronym.includes(queryLower) || queryLower.includes(gameAcronym))
  ) {
    return true;
  }

  return false;
}

/**
 * @param {string} game
 * @param {string} query - Lowercased trimmed
 * @param {Record<string, string>} gameAliases
 * @param {string[]} [popularOrder]
 */
export function matchScore(game, query, gameAliases, popularOrder = NAVBAR_SEARCH_POPULAR_ORDER) {
  const gl = game.toLowerCase();
  const pr = popularRank(game, popularOrder);
  if (gameAliases[query] === game) return 1_000_000;
  if (gl === query) return 950_000;
  if (gl.startsWith(query)) return 900_000 - gl.length;
  const words = gl.split(/[\s-]+/).filter(Boolean);
  if (words.some((w) => w.startsWith(query))) return 850_000 - pr * 10;
  if (query.length >= 3 && gl.includes(query)) return 700_000 - pr * 10;
  const acronym = words.map((w) => w[0]).join('');
  if (query.length >= 2 && (acronym.includes(query) || query.includes(acronym))) {
    return 600_000 - pr * 10;
  }
  return 100_000 - pr * 10;
}

export function sortGamesBySearchRelevance(
  games,
  query,
  gameAliases,
  popularOrder = NAVBAR_SEARCH_POPULAR_ORDER
) {
  const q = query.toLowerCase().trim();
  if (!q) return [...games];
  return [...games].sort((a, b) => {
    const sa = matchScore(a, q, gameAliases, popularOrder);
    const sb = matchScore(b, q, gameAliases, popularOrder);
    if (sb !== sa) return sb - sa;
    return popularRank(a, popularOrder) - popularRank(b, popularOrder);
  });
}

/**
 * Filter + rank games for a search box; optional limit (e.g. navbar top 8).
 * @param {string[]} allGames
 * @param {string} query - Raw or lowercased query (trimmed inside)
 * @param {Record<string, string>} gameAliases
 * @param {{ popularOrder?: string[], limit?: number }} [options]
 * @returns {string[]}
 */
export function rankedSearchGames(allGames, query, gameAliases, options = {}) {
  const { popularOrder = NAVBAR_SEARCH_POPULAR_ORDER, limit } = options;
  const q = query.toLowerCase().trim();
  if (!q) return [];

  const aliasMatch = gameAliases[q];
  if (aliasMatch && allGames.includes(aliasMatch)) {
    const single = [aliasMatch];
    return limit != null ? single.slice(0, limit) : single;
  }

  let games = allGames
    .filter((game) => matchesGame(game, q, gameAliases))
    .map((game) => ({ game, score: matchScore(game, q, gameAliases, popularOrder) }))
    .sort(
      (a, b) =>
        b.score - a.score ||
        popularRank(a.game, popularOrder) - popularRank(b.game, popularOrder)
    )
    .map((x) => x.game);

  if (limit != null) games = games.slice(0, limit);
  return games;
}

/**
 * Default category order within a game (accounts / items / boosting / top-ups / currency).
 * Lower = earlier when the query does not hint a specific category.
 */
export function navbarDefaultCategoryPathTier(path) {
  if (path.includes('/accounts')) return 0;
  if (path.includes('/items')) return 1;
  if (path.includes('/boosting')) return 2;
  if (path.includes('/top-ups')) return 3;
  if (path.includes('/currency')) return 4;
  return 5;
}

/** Negative when the query likely targets this category — sort it first for that game. */
export function navbarCategoryQueryBias(path, queryLower) {
  const q = queryLower.trim();
  if (!q) return 0;
  if (path.includes('/boosting') && (/\brank\b/.test(q) || q.includes('boost') || q.includes('elo')))
    return -20;
  if (path.includes('/accounts') && (q.includes('account') || /\bacc\b/.test(q))) return -20;
  if (path.includes('/items') && (q.includes('item') || q.includes('skin'))) return -20;
  if (path.includes('/top-ups') && (q.includes('top up') || q.includes('topup') || q.includes('gift')))
    return -20;
  if (path.includes('/currency') && (q.includes('curr') || q.includes('gold') || q.includes('coin')))
    return -20;
  return 0;
}

export function sortNavbarCategoriesForSearch(categories, queryLower) {
  return [...categories].sort((a, b) => {
    const ba = navbarCategoryQueryBias(a.path, queryLower);
    const bb = navbarCategoryQueryBias(b.path, queryLower);
    if (ba !== bb) return ba - bb;
    return navbarDefaultCategoryPathTier(a.path) - navbarDefaultCategoryPathTier(b.path);
  });
}

/** One row from each game in turn, then second row from each, etc. — avoids long same-game blocks. */
export function interleaveNavbarCategoryRows(perGameRowLists) {
  const max = Math.max(0, ...perGameRowLists.map((r) => r.length));
  const out = [];
  for (let i = 0; i < max; i++) {
    for (const rows of perGameRowLists) {
      if (rows[i]) out.push(rows[i]);
    }
  }
  return out;
}
